import React, { useState } from "react";
import usePremiumJobCreditsBreakUp from "../../hooks/premiumJobCreditsBreakUp";
import DiscountedCreditsView from "../DiscountedCreditsView";
import { ChevronDown } from "lucide-react";
import cx from "classnames";
import { PerksDrawback, PerksDrawbackProps } from "../JobType/v2/PerksDrawback";
import classes from "./premiumPopup.module.css";
import { JhPopupDrawer } from "designPackages/popupPackage";
import { JhText } from "designPackages/textPackage";
import { getDataInMonthDateCommaYear } from "utils/creditUtils";

type RenderButtons = () => JSX.Element;

interface PremiumPopupProps {
  hasCloseButton?: boolean;
  heading?: string;
  subHeading?: string;
  jobId?: number;
  open: boolean;
  renderButtons?: RenderButtons;
  onClose: () => void;
  categoryId?: number;
  cityId?: string;
  perksDrawbackList: PerksDrawbackProps;
}

const PremiumPopup = (props: PremiumPopupProps): JSX.Element => {
  const {
    hasCloseButton = true,
    open,
    heading,
    subHeading,
    renderButtons,
    onClose,
    jobId,
    categoryId,
    cityId,
    perksDrawbackList
  } = props;
  const { isSpecialPrice, mrc, discountedCredits } =
    usePremiumJobCreditsBreakUp({
      jobId,
      categoryId,
      cityId
    });
  const [perksExpanded, setPerksExpanded] = useState(false);
  const viewBenefitsClick = (e: React.MouseEvent<HTMLElement>): void => {
    setPerksExpanded(prev => !prev);
  };

  return (
    <>
      <JhPopupDrawer
        open={open}
        position="center"
        hasCloseButton={hasCloseButton}
        onClose={onClose}
        className="tw-root"
      >
        <div
          className={cx(
            "flex flex-col justify-center text-left",
            classes.popupRootChild
          )}
        >
          <div className="flex justify-between items-center">
            <div className="flex flex-col gap-1.5 basis-4/5">
              {isSpecialPrice ? (
                <JhText variant="caption1" color="discovery-foreground">
                  🎉 Special Price
                </JhText>
              ) : null}
              {heading ? (
                <JhText variant="emphasis3" color="foreground">
                  {heading}
                </JhText>
              ) : null}
            </div>
            <img width={67} height={50} src="/hire/coins.svg" alt="coins" />
          </div>
          {subHeading ? (
            <>
              <div className="mb-4" />
              <JhText variant="body2" color="muted-foreground">
                {subHeading}
              </JhText>
            </>
          ) : null}
          <div className="mb-5" />
          <div className="w-full border rounded-lg">
            <div className="flex justify-between items-center border-b px-4 pt-4 pb-3">
              <JhText variant="emphasis4" color="foreground">
                Premium Job{" "}
              </JhText>
              <DiscountedCreditsView
                mrc={mrc}
                discountedCredits={discountedCredits}
              />
            </div>
            <div
              onClick={viewBenefitsClick}
              className="flex p-4 justify-between cursor-pointer"
            >
              <div>
                <JhText variant="emphasis4" color="muted-foreground">
                  View all benefits
                </JhText>
              </div>
              <ChevronDown
                className={`transform transition-transform duration-200 ${
                  perksExpanded ? "rotate-180" : ""
                }`}
                size={16}
                color="var(--muted-foreground)"
              />
            </div>
            <div
              className={`${
                perksExpanded
                  ? "animate-fadeIn block"
                  : "animate-fadeOut hidden" // pending
              } px-4 pb-4 -mt-1.5`}
            >
              <PerksDrawback {...perksDrawbackList} />
            </div>
          </div>
          <div className="mt-5" />
          {renderButtons ? renderButtons() : null}
        </div>
      </JhPopupDrawer>
    </>
  );
};

export default PremiumPopup;
